import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'motion/react';
import confetti from 'canvas-confetti';
import { stableEmoji } from '../../types/passport';
import DetailSheet from './DetailSheet';
import type { Customer } from '../../contexts/CustomerAuthContext';
import type {
  PassportData,
  RestaurantProgress,
  Achievement,
  UpcomingAchievement,
} from '../../types/passport';

const REWARD_THRESHOLD = 12;

const LEVELS = [
  { key: 'passport.levelExplorer', min: 0, max: 9 },
  { key: 'passport.levelRegular', min: 10, max: 19 },
  { key: 'passport.levelPatron', min: 20, max: 49 },
  { key: 'passport.levelLegend', min: 50, max: Infinity },
];

const getLevelKey = (totalOrders: number): string => {
  if (totalOrders >= 50) return 'passport.levelLegend';
  if (totalOrders >= 20) return 'passport.levelPatron';
  if (totalOrders >= 10) return 'passport.levelRegular';
  return 'passport.levelExplorer';
};

// ── StampSheet ────────────────────────────────────────────────────────────────

function StampSheet({ stamp, onClose, color }: { stamp: RestaurantProgress; onClose: () => void; color: string }) {
  const { t } = useTranslation();

  const lastVisitDate = new Date(stamp.lastOrder).toLocaleDateString('ru-RU', {
    day: 'numeric', month: 'short',
  });

  return createPortal(
    <div className="pp-sheet-overlay" onClick={onClose}>
      <motion.div
        className="pp-sheet"
        initial={{ y: '100%' }}
        animate={{ y: 0 }}
        exit={{ y: '100%' }}
        transition={{ type: 'spring', stiffness: 300, damping: 30 }}
        onClick={e => e.stopPropagation()}
      >
        <div className="pp-sheet-handle" />

        {/* Header */}
        <div className="pp-sheet-head">
          <div className="pp-sheet-icon" style={{ borderColor: color, background: `${color}18` }}>
            {stableEmoji(stamp.id)}
          </div>
          <div>
            <div className="pp-sheet-title">{stamp.name}</div>
            <div className="pp-sheet-sub">{lastVisitDate}</div>
          </div>
        </div>

        {/* Stats */}
        <div className="pp-sheet-stats">
          <div className="pp-sheet-stat">
            <span className="pp-sheet-stat-val">{stamp.orders}</span>
            <span className="pp-sheet-stat-lbl">{t('foodPassport.orders')}</span>
          </div>
          <div className="pp-sheet-stat">
            <span className="pp-sheet-stat-val">{stamp.visitsThisMonth}</span>
            <span className="pp-sheet-stat-lbl">{t('passport.visitsThisMonth')}</span>
          </div>
        </div>

        {/* Achievement progress (only if restaurant has achievements) */}
        {stamp.achievementProgress && (
          <div className="pp-ach-block">
            <div className="pp-ach-block-header">
              <span className="pp-ach-block-title"> {t('passport.achievementProgress')}</span>
              <span className="pp-ach-block-pct">{stamp.achievementProgress.pct}%</span>
            </div>
            <div className="pp-ach-bar">
              <div className="pp-ach-bar-fill" style={{ width: `${stamp.achievementProgress.pct}%` }} />
            </div>
            <div className="pp-ach-hint">
              {stamp.achievementProgress.unlocked}/{stamp.achievementProgress.total}
              {stamp.achievementProgress.nextAchievementName && (
                <> · {t('passport.nextAchievement', { name: stamp.achievementProgress.nextAchievementName })}</>
              )}
            </div>
          </div>
        )}

        {/* Recent orders */}
        {stamp.recentOrders.length > 0 && (
          <>
            <div className="pp-sheet-section-label">{t('passport.recentOrders')}</div>
            {stamp.recentOrders.map(order => {
              const dishNames = order.items
                .map(it => `${it.name}${it.quantity > 1 ? ` ×${it.quantity}` : ''}`)
                .join(', ');
              const dateStr = new Date(order.createdAt).toLocaleDateString('ru-RU', {
                day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
              });
              return (
                <div key={order.id} className="pp-mini-order">
                  <div className="pp-mini-order-info">
                    <div className="pp-mini-order-dishes">{dishNames}</div>
                    <div className="pp-mini-order-date">{dateStr}</div>
                  </div>
                  <div className="pp-mini-order-total">
                    {Math.round(order.totalAmount).toLocaleString('ru-RU')} ₸
                  </div>
                </div>
              );
            })}
          </>
        )}
      </motion.div>
    </div>,
    document.body
  );
}

// ── AchievementOverlay ────────────────────────────────────────────────────────

function AchievementOverlay({
  achievement,
  onDone,
}: {
  achievement: { id: string; name: string; description: string; icon: string; points: number };
  onDone: () => void;
}) {
  const { t } = useTranslation();

  useEffect(() => {
    confetti({
      particleCount: 80,
      spread: 75,
      origin: { y: 0.45 },
      colors: ['#FF5A00', '#FF8C42', '#FCD34D', '#fff', '#a78bfa'],
      zIndex: 9991,
    });
  }, []);

  return createPortal(
    <motion.div
      className="pp-ach-overlay"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.25 }}
    >
      <motion.div
        className="pp-big-stamp"
        initial={{ scale: 0, rotate: -12 }}
        animate={{ scale: 1, rotate: 0 }}
        transition={{ type: 'spring', stiffness: 280, damping: 16, delay: 0.05 }}
      >
        <div className="pp-big-stamp-ink" />
        <span className="pp-big-stamp-icon">{achievement.icon}</span>
        <span className="pp-big-stamp-name">{achievement.name}</span>
      </motion.div>

      <motion.div
        className="pp-ach-overlay-title"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4, duration: 0.3 }}
      >
        {t('achievement.unlocked')}
      </motion.div>

      <motion.div
        className="pp-ach-overlay-pts"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.55, duration: 0.3 }}
      >
        +{achievement.points} pts
      </motion.div>

      <motion.div
        className="pp-ach-overlay-sub"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.7, duration: 0.3 }}
      >
        {achievement.description}
      </motion.div>

      <motion.button
        className="pp-ach-overlay-btn"
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.9, duration: 0.3 }}
        onClick={onDone}
      >
        {t('achievement.viewInPassport')}
      </motion.button>
    </motion.div>,
    document.body
  );
}

// ── PassportView ──────────────────────────────────────────────────────────────

interface PassportViewProps {
  data: PassportData;
  effectiveCustomer: Customer;
  // Achievement reveal
  revealAchIds: string[];
  showAchOverlayId: string | null;
  flyOutAchIds: string[];
  stampedAchIds: string[];
  onOverlayDone: () => void;
  // Stamp selection
  selectedStamp: RestaurantProgress | null;
  selectedStampColor: string;
  onSelectStamp: (stamp: RestaurantProgress, color: string) => void;
  onCloseStamp: () => void;
  // Badge detail
  selectedBadge: { id: string; restaurantId: string; badgeName: string } | null;
  badgeStats: {
    visitCount: number;
    currentTier: { name: string; threshold: number; imageUrl: string | null } | null;
    nextTier: { name: string; threshold: number; imageUrl: string | null } | null;
    favoriteDish: { id: string; name: string; imageUrl: string | null; timesOrdered: number } | null;
  } | null;
  badgeStatsLoading: boolean;
  onSelectBadge: (badge: { id: string; restaurantId: string; badgeName: string }) => void;
  onCloseBadge: () => void;
  fetchBadgeStats: (cId: string, rId: string) => void;
}

export default function PassportView({
  data,
  effectiveCustomer,
  revealAchIds,
  showAchOverlayId,
  flyOutAchIds,
  stampedAchIds,
  onOverlayDone,
  selectedStamp,
  selectedStampColor,
  onSelectStamp,
  onCloseStamp,
  selectedBadge,
  badgeStats,
  badgeStatsLoading,
  onSelectBadge,
  onCloseBadge,
  fetchBadgeStats,
}: PassportViewProps) {
  const { t } = useTranslation();

  const [showRewardDetail, setShowRewardDetail] = useState(false);
  const [selectedAchievement, setSelectedAchievement] = useState<Achievement | null>(null);
  const [selectedUpcoming, setSelectedUpcoming] = useState<UpcomingAchievement | null>(null);

  const d = data;
  const totalOrders = d.customer.totalOrders;
  const restaurants = d.restaurants;
  const levelName = t(getLevelKey(totalOrders));

  const STAMP_COLORS = ['#E53E3E', '#3B82F6', '#10B981', '#D4AF37', '#8B5CF6', '#0891B2'];

  const bestRestaurant = restaurants.length > 0
    ? restaurants.reduce((best, r) => r.orders > best.orders ? r : best, restaurants[0])
    : null;
  const nextReward = bestRestaurant && bestRestaurant.orders < REWARD_THRESHOLD
    ? { name: bestRestaurant.name, current: bestRestaurant.orders, target: REWARD_THRESHOLD }
    : null;

  return (
    <>
      {/* ── LEVEL PROGRESS ── */}
      {(() => {
        const currentLevel = LEVELS.slice().reverse().find(l => totalOrders >= l.min) || LEVELS[0];
        const nextLevel = LEVELS.find(l => l.min > totalOrders);
        if (!nextLevel) return null;
        const progress = Math.min(1, (totalOrders - currentLevel.min) / (nextLevel.min - currentLevel.min));
        return (
          <div className="pp-level-progress">
            <div className="pp-level-progress-labels">
              <span className="pp-level-current">{t(currentLevel.key)}</span>
              <span className="pp-level-next">{nextLevel.min - totalOrders} {t('passport.ordersToNextLevel')} → {t(nextLevel.key)}</span>
            </div>
            <div className="pp-level-bar">
              <motion.div
                className="pp-level-bar-fill"
                initial={{ scaleX: 0 }}
                animate={{ scaleX: progress }}
                transition={{ duration: 0.8, ease: 'easeOut', delay: 0.3 }}
                style={{ originX: 0 }}
              />
            </div>
          </div>
        );
      })()}

      {/* ── STAMPS ── */}
      <div className="pp-section-header">
        <span className="pp-section-title">{t('foodPassport.stamps')}</span>
      </div>
      <div className="pp-stamps-row">
        {restaurants.map((r, i) => {
          const color = STAMP_COLORS[i % 6];
          return (
            <motion.div
              key={r.id}
              className="pp-rstamp"
              style={{ ['--rs-tint' as string]: color } as React.CSSProperties}
              initial={{ scale: 0.3, rotate: -15, opacity: 0 }}
              animate={{ scale: 1, rotate: 0, opacity: 1 }}
              transition={{ type: 'spring', stiffness: 300, damping: 15, delay: i * 0.05 }}
              whileHover={{ y: -3 }}
              onClick={() => { onSelectStamp(r, color); }}
            >
              <div className="pp-rstamp-tint" aria-hidden />
              <div className="pp-rstamp-count">{r.orders}</div>
              <span className="pp-rstamp-emoji">{stableEmoji(r.id)}</span>
              <span className="pp-rstamp-name">{r.name.toUpperCase().slice(0, 10)}</span>
            </motion.div>
          );
        })}
      </div>

      {/* ── NEXT REWARD ── */}
      {nextReward && (
        <div className="pp-next-reward" onClick={() => setShowRewardDetail(true)} style={{ cursor: 'pointer' }}>
          <span className="pp-next-reward-icon"></span>
          <div className="pp-next-reward-info">
            <span className="pp-next-reward-label">{t('foodPassport.nextReward')}</span>
            <span className="pp-next-reward-title">{nextReward.name}</span>
            <div className="pp-next-reward-bar">
              <div
                className="pp-next-reward-bar-fill"
                style={{ width: `${Math.min(100, (nextReward.current / nextReward.target) * 100)}%` }}
              />
            </div>
            <span className="pp-next-reward-hint">
              {nextReward.target - nextReward.current} {t('foodPassport.ordersToReward')}
            </span>
          </div>
          <div className="pp-next-reward-count">
            {nextReward.target - nextReward.current}
            <span>{t('foodPassport.left')}</span>
          </div>
        </div>
      )}

      {/* ── REWARD DETAIL SHEET ── */}
      <DetailSheet open={showRewardDetail && !!nextReward} onClose={() => setShowRewardDetail(false)}>
        {nextReward && (
          <>
            <div className="pp-detail-header">
              <span className="pp-detail-icon">🎁</span>
              <div>
                <div className="pp-detail-title">{t('foodPassport.nextReward')}</div>
                <div className="pp-detail-subtitle">{nextReward.name}</div>
              </div>
            </div>

            <div className="pp-detail-desc">
              {t('passport.rewardCondition', { count: nextReward.target, restaurant: nextReward.name })}
            </div>

            <div className="pp-detail-progress-block">
              <div className="pp-detail-progress-labels">
                <span>{nextReward.current} / {nextReward.target}</span>
                <span>{Math.round((nextReward.current / nextReward.target) * 100)}%</span>
              </div>
              <div className="pp-detail-progress-bar">
                <div
                  className="pp-detail-progress-fill"
                  style={{ width: `${Math.min(100, (nextReward.current / nextReward.target) * 100)}%` }}
                />
              </div>
            </div>

            <div className="pp-detail-stat-row">
              <div className="pp-detail-stat-chip">
                <span className="pp-detail-stat-chip-val">{nextReward.target - nextReward.current}</span>
                <span className="pp-detail-stat-chip-lbl">{t('foodPassport.ordersToReward')}</span>
              </div>
            </div>
          </>
        )}
      </DetailSheet>

      {/* ── UNIFIED REWARDS GRID (badges + unlocked achievements + upcoming) ── */}
      {(d.badges.length + d.achievements.length + d.upcoming.length) > 0 && (
        <>
          <div className="pp-section-header">
            <span className="pp-section-title">{t('foodPassport.rewardsHeading', 'Награды и ачивки')}</span>
          </div>
        <div className="pp-rewards-grid">
          <AnimatePresence>
            {d.badges.map((badge, i) => (
              <motion.div
                key={`b-${badge.id}`}
                className="pp-reward pp-reward--badge"
                style={{ cursor: 'pointer' }}
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ type: 'spring', stiffness: 300, damping: 18, delay: i * 0.04 }}
                whileHover={{ y: -3 }}
                onClick={() => {
                  if (badge.restaurantId && effectiveCustomer?.id) {
                    onSelectBadge({ id: badge.id, restaurantId: badge.restaurantId, badgeName: badge.name });
                    fetchBadgeStats(effectiveCustomer.id, badge.restaurantId);
                  } else {
                    // Global badge — show as achievement-style detail
                    setSelectedAchievement({
                      id: badge.id,
                      name: badge.name,
                      description: badge.description || '',
                      icon: badge.icon || '🏅',
                      points: 0,
                      unlockedAt: new Date().toISOString(),
                    } as Achievement);
                  }
                }}
              >
                <div className="pp-reward-icon">{badge.icon || '🏅'}</div>
                <div className="pp-reward-name">{badge.name}</div>
                <div className="pp-reward-pin" aria-hidden>✓</div>
              </motion.div>
            ))}

            {d.achievements.map((a, i) => {
              const isNew = stampedAchIds.includes(a.id);
              return (
                <motion.div
                  key={`a-${a.id}`}
                  className={`pp-reward pp-reward--unlocked${isNew ? ' is-new' : ''}`}
                  style={{ cursor: 'pointer' }}
                  initial={isNew ? { scale: 0, rotate: -12, opacity: 0 } : { scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, rotate: 0, opacity: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  transition={
                    isNew
                      ? { type: 'spring', stiffness: 320, damping: 18 }
                      : { type: 'spring', stiffness: 300, damping: 18, delay: (d.badges.length + i) * 0.04 }
                  }
                  whileHover={{ y: -3 }}
                  onClick={() => setSelectedAchievement(a)}
                >
                  <div className="pp-reward-icon">{a.icon || '⭐'}</div>
                  <div className="pp-reward-name">{a.name}</div>
                  <div className="pp-reward-pin" aria-hidden>✓</div>
                  {isNew && (
                    <motion.div
                      className="pp-reward-new"
                      initial={{ opacity: 0, scale: 0 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ type: 'spring', stiffness: 400, damping: 18, delay: 0.6 }}
                    >NEW</motion.div>
                  )}
                </motion.div>
              );
            })}

            {d.upcoming.map((a, i) => {
              const isRevealing = revealAchIds.includes(a.id);
              const isFlyingOut = flyOutAchIds.includes(a.id);
              const pct = a.targetValue > 0 ? Math.min(100, (a.currentValue / a.targetValue) * 100) : 0;
              return (
                <motion.div
                  key={`u-${a.id}`}
                  className="pp-reward pp-reward--locked"
                  style={{ cursor: 'pointer' }}
                  initial={{ scale: 0.95, opacity: 0 }}
                  animate={isFlyingOut ? { opacity: 0, y: -30, scale: 0.9 } : { scale: 1, opacity: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  transition={isFlyingOut
                    ? { duration: 0.4, ease: 'easeIn' }
                    : { type: 'spring', stiffness: 300, damping: 18, delay: (d.badges.length + d.achievements.length + i) * 0.04 }
                  }
                  whileHover={{ y: -3 }}
                  onClick={() => setSelectedUpcoming(a)}
                >
                  <div className="pp-reward-icon pp-reward-icon--muted">{a.icon || '🔒'}</div>
                  <div className="pp-reward-name">{a.name}</div>
                  {a.targetValue > 0 && (
                    <div className="pp-reward-progress" aria-label={`${a.currentValue}/${a.targetValue}`}>
                      <motion.div
                        className="pp-reward-progress-fill"
                        initial={{ width: `${pct}%` }}
                        animate={{ width: isRevealing ? '100%' : `${pct}%` }}
                        transition={{ duration: 0.9, ease: [0.4, 0, 0.2, 1] }}
                      />
                    </div>
                  )}
                  {a.targetValue > 0 && (
                    <div className="pp-reward-progress-text">{a.currentValue}/{a.targetValue}</div>
                  )}
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>
        </>
      )}

      {restaurants.length === 0 && d.achievements.length === 0 && d.upcoming.length === 0 && (
        <div className="pp-onboarding">
          <div className="pp-onboarding-stamps">
            {Array.from({ length: 8 }).map((_, i) => (
              <motion.div
                key={i}
                className="pp-stamp-circle pp-stamp-circle--empty pp-onboarding-slot"
                initial={{ opacity: 0, scale: 0.6 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: i * 0.07, type: 'spring', stiffness: 260, damping: 18 }}
              >
                
              </motion.div>
            ))}
          </div>
          <div className="pp-onboarding-title">{t('passport.onboardingTitle')}</div>
          <div className="pp-onboarding-sub">{t('passport.onboardingSub')}</div>
        </div>
      )}

      {/* ── SHARE ── */}
      {restaurants.length > 0 && (
        <button
          type="button"
          className="pp-share-btn"
          onClick={async () => {
            const url = `${window.location.origin}/passport`;
            const text = `${t('passport.shareText')} — ${levelName}, ${totalOrders} ${t('foodPassport.orders')}, ${restaurants.length} ${t('foodPassport.yourRestaurants')}`;
            if (navigator.share) {
              try { await navigator.share({ title: 'OLSO Food Passport', text, url }); } catch { /* cancelled */ }
            } else {
              await navigator.clipboard.writeText(`${text}\n${url}`);
            }
          }}
        >
          <svg className="pp-share-btn-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/>
            <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
          </svg>
          <span className="pp-share-btn-text">{t('passport.share')}</span>
        </button>
      )}

      {/* ── ACHIEVEMENT OVERLAY ── */}
      <AnimatePresence>
        {showAchOverlayId && (() => {
          const ach = data?.achievements.find(a => a.id === showAchOverlayId);
          return ach ? (
            <AchievementOverlay
              key={showAchOverlayId}
              achievement={{ ...ach, icon: ach.icon || '', points: ach.points }}
              onDone={onOverlayDone}
            />
          ) : null;
        })()}
      </AnimatePresence>

      {/* ── STAMP SHEET ── */}
      <AnimatePresence>
        {selectedStamp && (
          <StampSheet stamp={selectedStamp} onClose={onCloseStamp} color={selectedStampColor} />
        )}
      </AnimatePresence>

      <div className="pp-bottom-pad" />

      {/* ── BADGE DETAIL ── */}
      <DetailSheet open={!!selectedBadge} onClose={onCloseBadge}>
        {selectedBadge && (
          <>
            <div className="pp-detail-header">
              <span className="pp-detail-icon">🏅</span>
              <div>
                <div className="pp-detail-title">{t('passport.badge')}</div>
                <div className="pp-detail-subtitle">{selectedBadge.badgeName}</div>
              </div>
            </div>

            {badgeStatsLoading ? (
              <div style={{ textAlign: 'center', padding: 32, color: 'var(--a-cream-300)' }}>{t('common.loading')}</div>
            ) : badgeStats ? (
              <>
                <div className="pp-detail-stat-row">
                  <div className="pp-detail-stat-chip">
                    <span className="pp-detail-stat-chip-val">{badgeStats.visitCount}</span>
                    <span className="pp-detail-stat-chip-lbl">{t('passport.visits')}</span>
                  </div>
                  {badgeStats.currentTier && (
                    <div className="pp-detail-stat-chip">
                      <span className="pp-detail-stat-chip-val">{badgeStats.currentTier.name}</span>
                      <span className="pp-detail-stat-chip-lbl">{t('passport.currentTier')}</span>
                    </div>
                  )}
                </div>

                {badgeStats.nextTier && (
                  <div className="pp-detail-progress-block" style={{ marginTop: 16 }}>
                    <div className="pp-detail-progress-labels">
                      <span>{badgeStats.visitCount} / {badgeStats.nextTier.threshold}</span>
                      <span>{Math.round((badgeStats.visitCount / badgeStats.nextTier.threshold) * 100)}%</span>
                    </div>
                    <div className="pp-detail-progress-bar">
                      <div
                        className="pp-detail-progress-fill"
                        style={{ width: `${Math.min(100, (badgeStats.visitCount / badgeStats.nextTier.threshold) * 100)}%` }}
                      />
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--a-cream-300)', marginTop: 6 }}>
                      {t('passport.nextTierHint', { name: badgeStats.nextTier.name })}
                    </div>
                  </div>
                )}

                {badgeStats.favoriteDish && (
                  <div className="pp-detail-rarity" style={{ marginTop: 16 }}>
                    {badgeStats.favoriteDish.imageUrl && (
                      <img
                        src={badgeStats.favoriteDish.imageUrl}
                        alt={badgeStats.favoriteDish.name}
                        style={{ width: 40, height: 40, borderRadius: 8, objectFit: 'cover' }}
                      />
                    )}
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--a-dark)' }}>{badgeStats.favoriteDish.name}</div>
                      <div style={{ fontSize: 12, color: 'var(--a-cream-300)' }}>
                        {t('passport.orderedTimes', { count: badgeStats.favoriteDish.timesOrdered })}
                      </div>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div style={{ textAlign: 'center', padding: 32, color: 'var(--a-cream-300)' }}>{t('passport.noData')}</div>
            )}
          </>
        )}
      </DetailSheet>

      {/* ── ACHIEVEMENT DETAIL ── */}
      <DetailSheet open={!!selectedAchievement} onClose={() => setSelectedAchievement(null)}>
        {selectedAchievement && (
          <>
            <div className="pp-detail-header">
              <span className="pp-detail-icon">{selectedAchievement.icon || '⭐'}</span>
              <div>
                <div className="pp-detail-title">{t('passport.achievement')}</div>
                <div className="pp-detail-subtitle">{selectedAchievement.name}</div>
              </div>
            </div>

            {selectedAchievement.description && (
              <div className="pp-detail-desc">{selectedAchievement.description}</div>
            )}

            <div className="pp-detail-stat-row">
              <div className="pp-detail-stat-chip">
                <span className="pp-detail-stat-chip-val">+{selectedAchievement.points}</span>
                <span className="pp-detail-stat-chip-lbl">{t('foodPassport.points')}</span>
              </div>
              <div className="pp-detail-stat-chip">
                <span className="pp-detail-stat-chip-val">
                  {new Date(selectedAchievement.unlockedAt).toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' })}
                </span>
                <span className="pp-detail-stat-chip-lbl">{t('passport.unlockedDate')}</span>
              </div>
            </div>
          </>
        )}
      </DetailSheet>

      {/* ── UPCOMING TASK DETAIL ── */}
      <DetailSheet open={!!selectedUpcoming} onClose={() => setSelectedUpcoming(null)}>
        {selectedUpcoming && (() => {
          const pct = selectedUpcoming.targetValue > 0
            ? Math.min(100, Math.round((selectedUpcoming.currentValue / selectedUpcoming.targetValue) * 100))
            : 0;
          return (
            <>
              <div className="pp-detail-header">
                <span className="pp-detail-icon">{selectedUpcoming.icon || '🎯'}</span>
                <div>
                  <div className="pp-detail-title">{t('passport.task')}</div>
                  <div className="pp-detail-subtitle">{selectedUpcoming.name}</div>
                </div>
              </div>

              {selectedUpcoming.description && (
                <div className="pp-detail-desc">{selectedUpcoming.description}</div>
              )}

              {selectedUpcoming.targetValue > 0 && (
                <div className="pp-detail-progress-block">
                  <div className="pp-detail-progress-labels">
                    <span>{selectedUpcoming.currentValue} / {selectedUpcoming.targetValue}</span>
                    <span>{pct}%</span>
                  </div>
                  <div className="pp-detail-progress-bar">
                    <div className="pp-detail-progress-fill" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              )}

              <div className="pp-detail-stat-row">
                <div className="pp-detail-stat-chip">
                  <span className="pp-detail-stat-chip-val">+{selectedUpcoming.points}</span>
                  <span className="pp-detail-stat-chip-lbl">{t('foodPassport.points')}</span>
                </div>
                {selectedUpcoming.targetValue > 0 && (
                  <div className="pp-detail-stat-chip">
                    <span className="pp-detail-stat-chip-val">{selectedUpcoming.targetValue - selectedUpcoming.currentValue}</span>
                    <span className="pp-detail-stat-chip-lbl">{t('passport.remaining')}</span>
                  </div>
                )}
              </div>
            </>
          );
        })()}
      </DetailSheet>
    </>
  );
}
